import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useUserRoles, ModeratorPermission } from "@/hooks/useUserRoles";

interface RequireRoleProps {
  children: ReactNode;
  /** If provided, user must hold at least one of these permissions (admins always pass). */
  anyPermission?: ModeratorPermission[];
  /** If true, admin role required (default false when anyPermission set). */
  adminOnly?: boolean;
}

/**
 * Defense-in-depth route guard for admin/moderator areas.
 * RLS enforces the real boundary; this just prevents UI exposure.
 */
export const RequireRole = ({
  children,
  anyPermission,
  adminOnly = false,
}: RequireRoleProps) => {
  const { isAdmin, can, isLoading } = useUserRoles();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-md p-8">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="h-64 w-full bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (adminOnly) {
    if (!isAdmin) return <Navigate to="/" replace />;
    return <>{children}</>;
  }

  const allowed =
    isAdmin || (anyPermission && anyPermission.some((p) => can(p)));

  if (!allowed) return <Navigate to="/" replace />;

  return <>{children}</>;
};

export default RequireRole;
